import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import type { Board } from '../types';

export function BoardsPage() {
  const { tenantId = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const [creating, setCreating] = useState(false);

  const boards = useQuery({
    queryKey: ['boards', tenantId],
    queryFn: () => api.get<Board[]>(`/tenants/${tenantId}/boards`),
  });

  return (
    <>
      <div className="page-head">
        <h1>Доски</h1>
        <div className="spacer" />
        <button
          className="btn btn-primary"
          disabled={catalog.queues.length === 0}
          onClick={() => setCreating(true)}
        >
          Новая доска
        </button>
      </div>

      {boards.isLoading && <div className="state">Загрузка…</div>}

      {catalog.queues.length === 0 && (
        <div className="card state">
          <div className="state-title">Сначала нужна очередь</div>
          Доска показывает задачи выбранных очередей, поэтому без очереди её не на чем построить.
          <div className="state-actions">
            <Link className="btn btn-primary" to={`/t/${tenantId}/queues`}>
              Перейти к очередям
            </Link>
          </div>
        </div>
      )}

      {catalog.queues.length > 0 && boards.data?.length === 0 && (
        <div className="card state">
          <div className="state-title">Досок пока нет</div>
          Доска раскладывает задачи очередей по колонкам статусов.
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              Создать доску
            </button>
          </div>
        </div>
      )}

      {boards.data && boards.data.length > 0 && (
        <div className="queue-grid">
          {boards.data.map((board) => (
            <Link key={board.id} to={`/t/${tenantId}/boards/${board.id}`} className="queue-card">
              <div className="queue-name">{board.name}</div>
              <div className="queue-meta">
                {board.boardType === 'SCRUM' ? 'Скрам' : 'Канбан'} · колонок: {board.columns.length} ·
                очередей: {board.queueIds.length}
              </div>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <CreateBoardModal tenantId={tenantId} queues={catalog.queues} onClose={() => setCreating(false)} />
      )}
    </>
  );
}

function CreateBoardModal({
  tenantId,
  queues,
  onClose,
}: {
  tenantId: string;
  queues: Array<{ id: string; key: string; name: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [boardType, setBoardType] = useState<'SCRUM' | 'KANBAN'>('SCRUM');
  const [queueIds, setQueueIds] = useState<string[]>(queues[0] ? [queues[0].id] : []);

  const create = useMutation({
    mutationFn: () => api.post<Board>(`/tenants/${tenantId}/boards`, { name, boardType, queueIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['boards', tenantId] });
      onClose();
    },
  });

  const toggleQueue = (id: string) =>
    setQueueIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  return (
    <Modal title="Новая доска" onClose={onClose}>
      <div className="form-group">
        <label className="form-label" htmlFor="board-name">
          Название
        </label>
        <input
          id="board-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoFocus
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="board-type">
          Тип
        </label>
        <select
          id="board-type"
          className="input"
          value={boardType}
          onChange={(event) => setBoardType(event.target.value as 'SCRUM' | 'KANBAN')}
        >
          <option value="SCRUM">Скрам — работа спринтами</option>
          <option value="KANBAN">Канбан — непрерывный поток</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Очереди на доске</label>
        {queues.map((queue) => (
          <label key={queue.id} className="row small" style={{ marginBottom: 5 }}>
            <input
              type="checkbox"
              checked={queueIds.includes(queue.id)}
              onChange={() => toggleQueue(queue.id)}
            />
            {queue.key} — {queue.name}
          </label>
        ))}
      </div>

      {create.isError && <div className="form-error">{(create.error as ApiError).message}</div>}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Отмена
        </button>
        <button
          className="btn btn-primary"
          disabled={!name.trim() || queueIds.length === 0 || create.isPending}
          onClick={() => create.mutate()}
        >
          Создать
        </button>
      </div>
    </Modal>
  );
}